import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, ImageIcon, X, Sparkles, ShieldCheck, Crown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { classifyError, openLimitModal, notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Markdown } from "@/components/Markdown";
import { TrustFooter } from "@/components/TrustFooter";
import { ActionCards } from "@/components/ActionCards";
import { SourceImages } from "@/components/SourceImages";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const MAX = 10 * 1024 * 1024;

export default function Assistant() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const isAuthed = user && typeof user === "object";

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX) { toast.error(t("error_generic")); return; }
    setFile(f);
    setResult(null);
    setSaved(false);
    setError(null);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const clear = () => { setFile(null); setPreview(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const analyze = async () => {
    if (!isAuthed) { navigate("/login"); return; }
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    const form = new FormData();
    form.append("files", file);
    form.append("prompt", prompt);
    form.append("language", language);
    try {
      const { data } = await api.post("/ai/analyze", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) {
      setError(classifyError(err)); // 429 also auto-opens the upgrade modal via the interceptor
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      await api.post("/saved-items", {
        type: "problem",
        title: file?.name?.slice(0, 120) || "Document help",
        content: result.answer,
        tags: ["document"],
        metadata: result.metadata || {},
      });
      setSaved(true);
      toast.success(t("result_saved"));
    } catch (err) {
      notifyError(err, t);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600"><ImageIcon className="h-6 w-6" /></span>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{t("assistant_title")}</h1>
            <p className="text-slate-500">{t("assistant_subtitle")}</p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          {!file ? (
            <button
              type="button"
              onClick={() => (isAuthed ? inputRef.current?.click() : navigate("/login"))}
              data-testid="assistant-dropzone"
              className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <Upload className="h-8 w-8 text-slate-400" />
              <span className="font-medium text-slate-700">{t("choose_file")}</span>
              <span className="text-xs text-slate-400">{t("assistant_hint")}</span>
            </button>
          ) : (
            <div data-testid="assistant-file-preview" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              {preview ? (
                <img src={preview} alt="preview" className="h-16 w-16 rounded-lg object-cover" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-white text-slate-400"><FileText className="h-7 w-7" /></span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
                <p className="text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button variant="ghost" size="sm" onClick={clear} data-testid="assistant-remove-btn" className="gap-1 text-slate-500">
                <X className="h-4 w-4" /> {t("remove_file")}
              </Button>
            </div>
          )}
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="assistant-file-input" onChange={(e) => pick(e.target.files?.[0])} />

          <div className="mt-4">
            <Label>{t("ask_about_file")}</Label>
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t("ask_about_file_ph")} data-testid="assistant-prompt-input" className="mt-1.5 min-h-[48px]" />
          </div>

          <Button onClick={analyze} disabled={!file || loading} data-testid="assistant-analyze-btn" className="mt-4 w-full gap-1.5 rounded-xl font-semibold min-h-[48px]">
            <Sparkles className="h-4 w-4" /> {loading ? t("analyzing") : isAuthed ? t("analyze_btn") : t("assistant_login")}
          </Button>
          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {t("assistant_disclaimer")}
          </p>
        </div>

        {loading && (
          <div className="mt-6 animate-pulse space-y-3" data-testid="assistant-loading">
            <div className="h-5 w-1/3 rounded bg-slate-100" />
            <div className="h-32 rounded-2xl bg-slate-100" />
          </div>
        )}

        {result && !loading && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="assistant-result">
            <Markdown content={result.answer} />
            <SourceImages images={result.metadata?.images} />
            <ActionCards actions={result.metadata?.actions} />
            <TrustFooter metadata={result.metadata} isAuthed={isAuthed} saved={saved} onSave={save} />
          </div>
        )}

        {error && !loading && (
          <div className="mt-6" data-testid="assistant-error">
            {error.kind === "limit" ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="assistant-error-limit">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-400/30 text-amber-700"><Crown className="h-5 w-5" /></span>
                  <div>
                    <p className="font-display font-semibold text-slate-900">
                      {t("err_limit_title").replace("{count}", 10).replace(/\{plan\}/g, "Free")}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-600">{t("err_limit_desc").replace(/\{plan\}/g, "Free")}</p>
                  </div>
                </div>
                <Button onClick={() => openLimitModal(error.tier || "free")} data-testid="assistant-upgrade-btn"
                  className="mt-4 gap-1.5 rounded-xl bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300">
                  <Sparkles className="h-4 w-4" /> {t("err_upgrade_cta")}
                </Button>
              </div>
            ) : error.kind === "too_large" || error.kind === "unsupported" ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5" data-testid="assistant-error-file">
                <p className="text-sm font-medium text-rose-700">
                  {error.kind === "too_large" ? t("err_too_large") : (error.detail || t("err_unsupported"))}
                </p>
                <Button variant="outline" size="sm" onClick={clear} data-testid="assistant-choose-another-btn" className="mt-3 gap-1.5 rounded-full">
                  {t("err_choose_another")}
                </Button>
              </div>
            ) : error.kind === "rate_limit" ? (
              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5" data-testid="assistant-error-rate">
                <p className="font-display font-semibold text-slate-900">{t("err_rate_title")}</p>
                <p className="mt-0.5 text-sm text-slate-600">{t("err_rate_desc")}</p>
                <Button variant="outline" size="sm" onClick={analyze} data-testid="assistant-rate-retry-btn" className="mt-3 gap-1.5 rounded-full">
                  <RotateCcw className="h-3.5 w-3.5" /> {t("retry")}
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5" data-testid="assistant-error-temporary">
                <p className="text-sm font-medium text-rose-700">{t("err_temporary")}</p>
                <Button variant="outline" size="sm" onClick={analyze} data-testid="assistant-retry-btn" className="mt-3 gap-1.5 rounded-full">
                  <RotateCcw className="h-3.5 w-3.5" /> {t("retry")}
                </Button>
              </div>
            )}
          </div>
        )}

        {!result && !loading && !error && (
          <p className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center text-slate-500" data-testid="assistant-empty">
            {t("assistant_empty")}
          </p>
        )}
      </main>
      <Footer />
    </div>
  );
}
